import {
  DialogFooter,
  DialogHeader,
  DialogClose,
  DialogTitle,
  FormControl,
  FormMessage,
  Typography,
  FormField,
  Checkbox,
  FormItem,
  Loading,
  Stepper,
  Button,
  Form,
} from '@ui/components';
import { useMobilityHubPrivate } from '@content-api/provider';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FC } from 'react';
import { z } from 'zod';

interface DowngradeModalStep2Props {
  onConfirm: ({ answersIds }: { answersIds: string[] }) => void;
  isPending: boolean;
}

export const DowngradeModalStep2: FC<DowngradeModalStep2Props> = ({ onConfirm, isPending }) => {
  const { getSubscriptionDowngradeQuestions } = useMobilityHubPrivate();
  const { t } = useTranslation();

  const { data: downgradeFormulaQuestions, isLoading: isDowngradeFormulaQuestionsLoading } =
    useQuery({
      queryKey: ['downgrade-formula-questions'],
      queryFn: () => getSubscriptionDowngradeQuestions(),
    });

  const form = useForm({
    resolver: zodResolver(
      z.object({
        answersIds: z
          .array(z.string())
          .min(1, { message: t('SUBSCRIPTIONS.DOWNGRADE_MODAL.SELECT_AT_LEAST_ONE') }),
      }),
    ),
    defaultValues: { answersIds: [] },
    mode: 'onBlur',
  });

  return (
    <Loading isLoading={isDowngradeFormulaQuestionsLoading}>
      <DialogHeader>
        <DialogTitle>{t('SUBSCRIPTIONS.DOWNGRADE_MODAL.ANSWER_THE_QUESTION')}</DialogTitle>
      </DialogHeader>
      <div className="px-4 pt-4">
        <Stepper className="mb-4" activeStep={1} steps={2} />

        <Typography variant="labelLargeBook">
          {t('SUBSCRIPTIONS.DOWNGRADE_MODAL.ANSWER_QUESTIONS_DESCRIPTION')}
        </Typography>

        <Form {...form}>
          <FormField
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-col gap-4 mt-4">
                      {downgradeFormulaQuestions?.data?.map((question) => (
                        <div className="flex items-center gap-4" key={question.id}>
                          <Checkbox
                            onCheckedChange={() => {
                              if ((field.value as string[]).includes(question.code)) {
                                field.onChange(
                                  (field.value as string[]).filter((id) => id !== question.code),
                                );
                              } else {
                                field.onChange([...(field.value as string[]), question.code]);
                              }
                            }}
                            checked={(field.value as string[]).includes(question.code)}
                            key={question.code}
                            id={question.code}
                          />

                          <Typography variant="labelMediumMedium" asChild>
                            <label htmlFor={question.code}>{question.question}</label>
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </FormControl>

                  <FormMessage className="mt-4" />
                </FormItem>
              );
            }}
            control={form.control}
            name="answersIds"
          />
        </Form>

        <Typography className="mt-6 text-weak" variant="labelSmallBook">
          {t('SUBSCRIPTIONS.MODAL_DESCRIPTION')}
        </Typography>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button disabled={isPending} variant="tertiary" size="large" wide>
            {t('SUBSCRIPTIONS.DOWNGRADE_MODAL.CANCEL')}
          </Button>
        </DialogClose>
        <Button
          onClick={form.handleSubmit(onConfirm)}
          isLoading={isPending}
          disabled={isPending}
          type="button"
          size="large"
          wide
        >
          {t('SUBSCRIPTIONS.DOWNGRADE_MODAL.SAVE')}
        </Button>
      </DialogFooter>
    </Loading>
  );
};
